<template>
  <div class="p-5 text-left bg-white" @click="$emit('on-click-comment')">
    <!-- ユーザーアイコン・タイトル・ユーザーID -->
    <div class="flex items-center">
      <!-- 投稿者アイコン -->
      <img
        :src="getUserIcon"
        class="h-8 w-8 rounded-[16px] shadow-icon mr-2"
        @error="guestImgOnError"
      />
      <!-- 投稿者ID -->
      <div
        class="text-W4 text-[11px] leading-[11px] text-gray400 mt-1 min-w-0 break-words"
      >
        {{ comment.contributorId }}
      </div>
    </div>
    <!-- 地点情報 -->
    <div v-show="isShowSpotInfo" class="mt-4 flex">
      <img src="@/assets/Icon_Map_Place.svg" class="w-3 h-3 mr-2" />
      <div class="flex-1 w-full overflow-hidden">
        <div class="text-W7 text-[13px] leading-[13px] truncate mb-1">
          {{ comment.spot.name }}
        </div>
        <div
          class="text-W4 text-[11px] leading-[11px] truncate text-gray400 truncate"
        >
          {{ comment.spot.address }}
        </div>
      </div>
    </div>
    <!-- サムネイル画像 -->
    <img
      :src="getThumbnail"
      class="w-full mt-4 rounded-[8px]"
      @error="thumbnailImgOnError"
    />
    <div class="mt-4">
      <!-- 記事タイトル(2行まで表示) -->
      <div class="break-words">
        <div
          class="text-W7 text-[15px] leading-[19px] text-gray600 titleLineBreak"
        >
          {{ comment.title }}
        </div>
      </div>
      <!-- 記事本文 -->
      <div
        ref="comment-body"
        class="text-[13px] text-W4 leading-[18px mt-4"
        :class="{bodyLineBreak: !showFullText}"
        @click.stop="showMoreOrEmitClick()"
      >
        {{ comment.body }}
      </div>
      <div
        v-if="isOverflowing && !showFullText"
        class="text-gray400 text-[13px] leading-[17.5px] text-W4"
        @click.stop="showMoreOrEmitClick()"
      >
        続きを見る
      </div>
    </div>
    <!-- タグ -->
    <div class="flex mt-4">
      <div class="flex-1 flex gap-2 flex-wrap w-full">
        <div v-for="tag in comment.tags" :key="tag" class="w-fit max-w-full">
          <BaseTag :tagName="tag" />
        </div>
      </div>
    </div>
    <!-- 投稿日 -->
    <div
      class="mt-4 text-right text-W4 text-[11px] leading-[11px] text-gray400"
    >
      {{ getFormatYearDateSlash(comment.createAt) }}
    </div>
  </div>
</template>
<script>
import BaseTag from '@/components/atoms/BaseTag.vue'
import Util from '@/mixins/util'

const CommentCard = {
  name: 'CommentCard',
  components: {BaseTag},
  data() {
    return {
      isOverflowing: false, // 本文が表示領域からはみ出すか
      showFullText: false, // 全文表示の有効可否
    }
  },
  props: {
    /** コメント情報 */
    comment: {
      type: Object,
      required: true,
    },
    /** 地点情報の表示可否 */
    isShowSpotInfo: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: ['on-click-comment'],
  mounted() {
    // 本文の全文表示可否を判定
    this.checkBodyOverflow()
  },
  computed: {
    /**
     * 投稿者アイコン
     */
    getUserIcon() {
      // ユーザーアイコン未設定の場合、ゲストアイコンを表示
      if (this.comment.publisherImg == '') {
        return require('@/assets/menu/icon_guest.svg')
      }

      return this.comment.publisherImg
    },
    /**
     * サムネイル画像
     */
    getThumbnail() {
      return this.comment.thumbnailImg
        ? this.comment.thumbnailImg
        : require('@/assets/NoImage.svg')
    },
  },
  methods: {
    /**
     * サムネイル画像読み込み失敗時処理
     * @param {Event} event イベント情報
     */
    thumbnailImgOnError(event) {
      // 読み込み失敗した場合は、NoImageアイコンを表示する
      const noImageIcon = require('@/assets/NoImage.svg')
      event.target.src = noImageIcon
    },
    /**
     * 投稿者画像読み込み失敗時処理
     * @param {Event} event イベント情報
     */
    guestImgOnError(event) {
      // 読み込み失敗した場合は、ゲストアイコンを表示する
      const guestIcon = require('@/assets/Icon_Guest.svg')
      event.target.src = guestIcon
    },
    /**
     * 本文が表示し切れるかどうかを判断し、結果を保持
     */
    checkBodyOverflow() {
      const textContainer = this.$refs['comment-body']
      this.isOverflowing =
        textContainer.scrollHeight > textContainer.clientHeight
    },
    /**
     * [YYYY/MM/DD]の形式で日付表記をフォーマット
     * ※ mixinsを使わない形式に今後移行する必要がある為、mixins経由ではなくimportしたモジュールを直接利用。
     * @param {String} date 日付文字列
     * @returns {String} フォーマット後の日付文字列
     */
    getFormatYearDateSlash(date) {
      return Util.methods.getFormatYearDateSlash(date)
    },
    /**
     * 本文押下時処理
     * 「続きを見る」が表示されている場合、本文を開く
     * 表示されていない場合は、親コンポーネントに押下通知を投げる
     */
    showMoreOrEmitClick() {
      if (this.isOverflowing && !this.showFullText) {
        this.showFullText = true
      } else {
        this.$emit('on-click-comment')
      }
    },
  },
}
export default CommentCard
</script>
<style scoped>
.titleLineBreak {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.bodyLineBreak {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
